import {
  useAreaGenerateStore,
  useCustomAlertStore,
  usePathStore,
  usePointStore,
} from "../../../../Store/store";
import { handleObstacleReset as doObstacleReset } from "../../PathRemove_Create";

/**
 * 장애물 추가/클릭 하이라이트/삭제/초기화
 * Pathgenerate 장애물 관련 로직만 분리
 */
export function useObstacle() {
  const { showInfoAlert, showWarningAlert } = useCustomAlertStore();
  const { selectedAreaType } = useAreaGenerateStore();
  const { selectedObstacleId, setSelectedObstacleId } = usePointStore();
  const {
    viewer,
    allObstaclePoints,
    setAllObstaclePoints,
    closedObstacleArea,
    setClosedObstacleArea,
    setShowOverlay,
    setObstacleMode,
    setPathGenerateMode,
    setPathEditMode,
    setOverlayMessage,
    selectedEquipmentId,
  } = usePathStore();

  const handleObstacleAdd = () => {
    if (!selectedAreaType) {
      showInfoAlert("영역 추가 실패", "카테고리를 선택해주세요.");
      return;
    }
    setShowOverlay(true);
    setObstacleMode(true);
    setPathGenerateMode(false);
    setPathEditMode(false);
    setOverlayMessage("장애물 좌표를 클릭해주세요.");
  };

  const handleObstacleClick = (id) => {
    setSelectedObstacleId(id);

    // 모든 장애물 라인을 기본 색상으로 초기화
    viewer.scene.scene.traverse((obj) => {
      if (obj.userData?.obstacleId && obj.userData?.type === "final-line") {
        const originalColor = obj.userData.originalColor ?? 0xff0000;
        const hex =
          typeof originalColor === "string"
            ? parseInt(originalColor.replace("#", ""), 16)
            : originalColor;
        obj.material.color.setHex(hex);
      }
    });

    // 선택된 장애물 라인만 강조 색상으로 변경
    viewer.scene.scene.traverse((obj) => {
      if (
        obj.userData?.obstacleId === id &&
        obj.userData?.type === "final-line"
      ) {
        obj.material.color.setHex(0xa6ff00);
      }
    });
  };

  // 장애물 id 기반으로 삭제 처리
  const handleRemoveObstacle = (id) => {
    // 장애물 배열에서 삭제할 장애물 인덱스 찾기
    const obstacleIndex = allObstaclePoints.findIndex(
      (obstacle) => obstacle.id === id,
    );

    // 장애물 인덱스가 -1이면 삭제할 장애물을 찾을 수 없음
    if (obstacleIndex === -1) {
      showInfoAlert("장애물 삭제 실패", "삭제할 장애물을 찾을 수 없습니다.");
      return;
    }

    const obstacleToDelete = allObstaclePoints[obstacleIndex]; // 삭제할 장애물

    const newAllObstaclePoints = [...allObstaclePoints]; // 장애물 배열 복사
    newAllObstaclePoints.splice(obstacleIndex, 1); // 장애물 배열에서 삭제
    setAllObstaclePoints(newAllObstaclePoints); // 장애물 배열 업데이트

    const newClosedObstacleArea = [...closedObstacleArea]; // 장애물 완성 좌표 배열 복사
    newClosedObstacleArea.splice(obstacleIndex, 1); // 장애물 완성 좌표 배열에서 삭제
    setClosedObstacleArea(newClosedObstacleArea); // 장애물 완성 좌표 배열 업데이트

    // 마커 삭제
    obstacleToDelete.markers.forEach((marker) => {
      if (marker && viewer.scene.scene) {
        viewer.scene.scene.remove(marker);
        if (marker.geometry) {
          marker.geometry.dispose();
        }
        if (marker.material) {
          marker.material.dispose();
        }
      }
    });

    if (obstacleToDelete.polygons && obstacleToDelete.polygons.length) {
      // 장애물 폴리곤 제거
      obstacleToDelete.polygons.forEach((obj) => {
        if (!obj) return;
        const scene = viewer?.scene?.scene;
        // 장애물 폴리곤 제거
        if (scene) viewer.scene.scene.remove(obj);
        // 장애물 폴리곤 기하 제거
        if (obj.geometry) obj.geometry.dispose();
        // 장애물 폴리곤 재료 제거
        if (obj.material) {
          // 배열일수도 있어서 안전하게 제거
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m) => m?.dispose && m.dispose());
          } else {
            obj.material.dispose();
          }
        }

        if (scene) {
          const leftovers = [];
          scene.traverse((o) => {
            if (o?.userData?.obstacleId === id || o?.userData?.preview)
              leftovers.push(o);
          });
          leftovers.forEach((o) => {
            scene.remove(o);
            o.geometry.dispose();
            if (Array.isArray(o.material)) {
              o.material.forEach((m) => {
                m?.dispose?.();
              });
            } else {
              o.material?.dispose();
            }
          });
        }
      });
      obstacleToDelete.renderObjects = [];
    }
    // 마커 참조도 정리
    obstacleToDelete.markers = [];
    obstacleToDelete.polygons = [];
    obstacleToDelete.lines = [];
    obstacleToDelete.all = [];
    // 포인트 등 필요 없으면:
    obstacleToDelete.points = [];
  };

  const handleObstacleReset = (e) => {
    e.stopPropagation();
    showWarningAlert("영역 초기화", "모든 영역을 초기화하시겠습니까?", () => {
      doObstacleReset(
        viewer,
        setAllObstaclePoints,
        setClosedObstacleArea,
        selectedEquipmentId,
      );
    });
  };

  return {
    handleObstacleAdd,
    handleObstacleClick,
    handleRemoveObstacle,
    handleObstacleReset,
  };
}
